import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { api } from '../api/client'
import type { CustomerGrant, Principal } from '../api/types'
import { decodeJwt, isExpired } from '../lib/jwt'

interface AuthState {
  identityToken: string | null
  accessToken: string | null
  customer: CustomerGrant | null
  principal: Principal | null
  /** Step 1: identity login with username + password. Returns the MFA
   *  challenge when the user has confirmed factors (identity_token null) —
   *  complete it, then call loginWithIdentityToken. */
  login: (username: string, password: string) => Promise<{ mfa_challenge: string; mfa_methods: string[] } | null>
  /** Step 1 (alternative): service-account login. */
  loginService: (serviceAccount: string, secret: string) => Promise<void>
  /** Step 1 (alternative): store an identity token obtained via the OAuth callback. */
  loginWithIdentityToken: (identityToken: string) => Promise<void>
  /** Step 2: exchange the identity token for a customer-scoped token. */
  selectCustomer: (uid: string) => Promise<void>
  /** Re-exchange the current customer's scope for a fresh customer token. Throws on failure. */
  renewCustomerToken: () => Promise<void>
  /** Clear the customer scope only (identity may still be valid). */
  expireSession: () => void
  /** Full sign-out. */
  logout: () => void
  /** Replace the stored principal (e.g. from a PATCH /me response) — the
   *  sidebar "this is you" chip re-renders immediately. */
  setPrincipal: (principal: Principal) => void
  /** Re-fetch /me into the store. */
  refreshPrincipal: () => Promise<void>
}

function fallbackPrincipal(identityToken: string, username: string): Principal {
  const sub = decodeJwt(identityToken)?.sub ?? ''
  return {
    unique_id: sub,
    kind: username.startsWith('osa-') ? 'service_account' : 'user',
    username,
    email: null,
    first_name: '',
    last_name: '',
    is_superadmin: false,
    last_login: null,
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      identityToken: null,
      accessToken: null,
      customer: null,
      principal: null,

      async login(username, password) {
        const res = await api.login(username, password)
        if (!res.identity_token) {
          // MFA gate: the password alone mints no token — the caller runs the
          // second factor, then loginWithIdentityToken.
          return { mfa_challenge: res.mfa_challenge ?? '', mfa_methods: res.mfa_methods ?? [] }
        }
        const identity_token = res.identity_token
        set({ identityToken: identity_token, accessToken: null, customer: null })
        // Best effort — /me may not exist yet on the backend.
        let principal = fallbackPrincipal(identity_token, username)
        try {
          principal = await api.me()
        } catch {
          /* endpoint pending */
        }
        set({ principal })
        return null
      },

      async loginService(serviceAccount, secret) {
        const { identity_token } = await api.loginService(serviceAccount, secret)
        if (!identity_token) throw new Error('Sign-in failed.')
        set({ identityToken: identity_token, accessToken: null, customer: null })
        let principal = fallbackPrincipal(identity_token, serviceAccount)
        try {
          principal = await api.me()
        } catch {
          /* endpoint pending */
        }
        set({ principal })
      },

      async loginWithIdentityToken(identityToken) {
        set({ identityToken, accessToken: null, customer: null })
        // No username is known for OAuth identities — fall back to the token sub.
        let principal = fallbackPrincipal(identityToken, decodeJwt(identityToken)?.sub ?? '')
        try {
          principal = await api.me()
        } catch {
          /* endpoint pending */
        }
        set({ principal })
      },

      async selectCustomer(uid) {
        const { access_token } = await api.token(uid)
        const customers = await api.customers().catch(() => [] as CustomerGrant[])
        const grant = customers.find((c) => c.unique_id === uid)
        set({
          accessToken: access_token,
          customer: grant ?? { unique_id: uid, name: uid, slug: uid, role: '' },
        })
      },

      async renewCustomerToken() {
        const { customer } = get()
        if (!customer) throw new Error('No customer selected')
        const { access_token } = await api.token(customer.unique_id)
        // A customer switch mid-exchange must not clobber the new scope's token.
        if (get().customer?.unique_id === customer.unique_id) set({ accessToken: access_token })
      },

      expireSession() {
        set({ accessToken: null, customer: null })
      },

      logout() {
        set({ identityToken: null, accessToken: null, customer: null, principal: null })
      },

      setPrincipal(principal) {
        set({ principal })
      },

      async refreshPrincipal() {
        const principal = await api.me()
        set({ principal })
      },
    }),
    {
      name: 'ostrel-auth',
      // localStorage, not sessionStorage: tabs each get their own
      // sessionStorage, which forced a fresh login per tab. Tokens are
      // 15-min JWTs regardless; multi-tab is the expected workflow.
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        identityToken: s.identityToken,
        accessToken: s.accessToken,
        customer: s.customer,
        principal: s.principal,
      }),
    },
  ),
)

/** True when there is a non-expired customer-scoped token. */
export function hasValidCustomerToken(): boolean {
  return !isExpired(useAuthStore.getState().accessToken)
}

/** True when the identity token is still usable (step 2 can resume). */
export function hasValidIdentityToken(): boolean {
  return !isExpired(useAuthStore.getState().identityToken)
}

/** Milliseconds until the current customer token expires; null when no token or no exp. */
export function msUntilCustomerTokenExpiry(): number | null {
  const token = useAuthStore.getState().accessToken
  if (!token) return null
  const exp = decodeJwt(token)?.exp
  if (!exp) return null
  return exp * 1000 - Date.now()
}
